import { useEffect, useState } from "react";
import { Pages } from "./page_list.js";
import { flows } from "./flow_template.js";
import { Question } from "./quiz-flow/pages/Question.jsx";
import { Navigation } from "./quiz-flow/Navigation.jsx";
import classNames from "classnames";
import { Statement as Statement230k } from "./quiz-flow/pages/Statement230kPlaces.jsx";
import { Statement as TagsStatement } from './quiz-flow/pages/TagsStatement.jsx';
import { PreCheckout } from "./quiz-flow/pages/PreCheckout.jsx";
import { EmailScreen } from './quiz-flow/pages/EmailScreen1.jsx';
import { Statement as SafetyStatement } from './quiz-flow/pages/SafetyRankingStatement.jsx';
import { Statement as CommunityStatement } from './quiz-flow/pages/CommunityValidationStatement.jsx';
import { Statement as CoverageStatement } from './quiz-flow/pages/GlobalCoverageStatement.jsx';
import { Statement as AggregatedStatement } from './quiz-flow/pages/AggregatedInfoStatement.jsx';
import { Statement as DietitianStatement } from './quiz-flow/pages/DietitianStatement.jsx';
import { Statement as RealtimeStatement } from './quiz-flow/pages/RealtimeUpdatesStatement.jsx';
import { Statement as CrossContaminationStatement } from './quiz-flow/pages/CrossContaminationStatement.jsx';
import { Statement as TravelStatement } from './quiz-flow/pages/TravelFeaturesStatement.jsx';
import { Statement as AtlyIntro } from './quiz-flow/pages/AtlyIntro1.jsx';
import { Statement as SafetyRankingExplanation } from './quiz-flow/pages/SafetyRankingExplanation.jsx';
import { Statement as ReviewAggregationStatement } from './quiz-flow/pages/ReviewAggregationStatement.jsx';
import { Statement as InformationUpdateStatement } from './quiz-flow/pages/InformationUpdateStatement.jsx';
import { Statement as SafetyCommitmentStatement } from './quiz-flow/pages/SafetyCommitmentStatement.jsx';
import { isEmailValid } from "@/utils/email";
import { QuizStripeEnricher } from "@/ui-only-components/QuizStripeEnricher";

export const FullScreenQuizFlow = ({
    isFullPageSliderOpen,
    setIsFullPageSliderOpen,
    initialAnswer,
    formSubmit,
    trackAction,
    trackView,
    flowVersion = "flow_a",
    flowPages,
    trialVariation = false,
}) => {
    console.log("FullScreenQuizFlow", { flowVersion });
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [answerHistory, setAnswerHistory] = useState([]);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [transitionDirection, setTransitionDirection] = useState(null);
    const [showNavigation, setShowNavigation] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isInitialTransition, setIsInitialTransition] = useState(true);
    const [userEmail, setUserEmail] = useState("");

    const currentPageName = flowPages[currentPageIndex];
    const totalPages = flowPages.length;

    const currentPage = Pages[currentPageName];

    useEffect(() => {
        if (isFullPageSliderOpen) {
            setTimeout(() => {
                requestAnimationFrame(() => {
                    setIsVisible(true);
                });
            }, 50);

            if (initialAnswer && currentPageIndex === 0) {
                setAnswerHistory([initialAnswer]);

                setTimeout(() => {
                    handleFirstAnswer(initialAnswer);
                }, 700);
            }
        } else {
            setIsVisible(false);
            setIsInitialTransition(true);
        }
    }, [isFullPageSliderOpen, initialAnswer]);

    useEffect(() => {
        if (isFullPageSliderOpen && currentPage) {
            trackView({
                page_name: currentPageName,
                page_type: currentPage.type,
                answers: answerHistory[currentPageIndex] || null,
                flow_version: flowVersion,
            });
        }
    }, [currentPageIndex, isFullPageSliderOpen]);

    const handleFirstAnswer = (answer, event) => {
        trackAction({
            page_name: currentPageName,
            action: "Question Answered",
            value_submitted: Array.isArray(answer)
                ? answer.map((a) => a.text)
                : [answer.text],
            flow_version: flowVersion,
        });

        const position = event?.clientX
            ? { x: event.clientX, y: event.clientY }
            : { x: window.innerWidth / 2, y: window.innerHeight / 2 };

        setTimeout(() => {
            setTransitionDirection("forward");
            setIsTransitioning(true);

            setTimeout(() => {
                setCurrentPageIndex((prev) => prev + 1);
                setIsTransitioning(false);
                setShowNavigation(true);
            }, 300);
        }, 1000);
    };

    const handleAnswer = (answer, event) => {
        trackAction({
            page_name: currentPageName,
            action: "Question Answered",
            value_submitted: Array.isArray(answer)
                ? answer.map((a) => a.text)
                : [answer.text],
            flow_version: flowVersion,
        });

        console.log("handleAnswer 5", { currentPageName, answer, userEmail });

        if (currentPageName === 'EmailScreen1' || currentPageName === 'EmailScreen2') {
            const email = answer.text;
            if (isEmailValid(email)) {
                setUserEmail(email);
            }
        }

        const position = event?.clientX
            ? { x: event.clientX, y: event.clientY }
            : { x: window.innerWidth / 2, y: window.innerHeight / 2 };

        setAnswerHistory((prev) => {
            const newHistory = [...prev.slice(0, currentPageIndex), answer];
            return newHistory;
        });

        if (currentPageIndex < totalPages - 1) {
            setTransitionDirection("forward");
            setIsTransitioning(true);

            setTimeout(() => {
                setCurrentPageIndex((prev) => prev + 1);
                setTimeout(() => {
                    setIsTransitioning(false);
                }, 50);
            }, 400);
        } else {
            formSubmit(answerHistory);
            setIsFullPageSliderOpen(false);
        }
    };

    const handleBack = () => {
        if (currentPageIndex > 0) {
            setIsInitialTransition(false);
            setTransitionDirection("back");
            setIsTransitioning(true);

            setTimeout(() => {
                setCurrentPageIndex((prev) => prev - 1);
                setIsTransitioning(false);
            }, 300);
        } else if (currentPageIndex === 0) {
            setIsVisible(false);
            setTimeout(() => {
                setIsFullPageSliderOpen(false);
                setCurrentPageIndex(0);
                setAnswerHistory([]);
                setShowNavigation(false);
            }, 500);
        }
    };

    const handleSkip = () => {
        trackAction({
            page_name: currentPageName,
            action: "Skip Clicked",
            value_submitted: null,
            flow_version: flowVersion,
        });

        if (currentPageIndex < totalPages - 1) {
            setTransitionDirection("forward");
            setIsTransitioning(true);

            setTimeout(() => {
                setCurrentPageIndex((prev) => prev + 1);
                setIsTransitioning(false);
            }, 500);
        }
    };

    const shouldShowSkip = () => {
        if (!currentPage) return false;

        switch (currentPage.type) {
            case 'Email':
                return false;
            case 'Statement':
                return false;
            case 'Question':
                return currentPage.allowSkip !== false;
            default:
                return false;
        }
    };

    const shouldShowNavigation = () => {
        if (!currentPage) return false;

        switch (currentPage.type) {
            case 'Email':
                return false;
            case 'Statement':
                return false;
            case 'Question':
                return currentPage.allowSkip !== false;
            case 'PreCheckout':
                return true;
            default:
                return false;
        }
    };

    const shouldShowProgressDots = () => {
        if (!currentPage) return false;
        return currentPage.type !== 'PreCheckout';
    };

    if (!isFullPageSliderOpen) return null;

    return (
        <>
            <QuizStripeEnricher />
            <div
                className={classNames(
                    "fixed inset-0 z-50 pt-16",
                    "transition-all duration-300",
                    {
                        "opacity-0 translate-y-full pointer-events-none": !isVisible,
                        "opacity-100 translate-y-0": isVisible,
                        "pointer-events-none": isTransitioning,
                    }
                )}
                style={{
                    background:
                        "linear-gradient(80deg, rgba(240, 30, 111, 0.05) 12.92%, rgba(248, 94, 74, 0.05) 94.79%), #FFF",
                }}>
                {showNavigation && currentPage && (
                    <Navigation
                        onBack={handleBack}
                        showBack={true}
                        onSkip={handleSkip}
                        showSkip={shouldShowSkip()}
                        totalSteps={totalPages}
                        currentStep={currentPageIndex + 1}
                        isFirstQuestion={currentPageIndex === 0}
                        showProgressDots={shouldShowProgressDots()}
                    />
                )}

                {currentPage && currentPage.type === "Question" && (
                    <Question
                        data={currentPage}
                        onAnswer={currentPageIndex === 0 ? handleFirstAnswer : handleAnswer}
                        flowVersion={flowVersion}
                        previousAnswer={answerHistory[currentPageIndex]}
                        isTransitioning={isTransitioning}
                        direction={transitionDirection}
                        forceAnswerAnimation={currentPageIndex === 0 && isInitialTransition}
                    />
                )}
                {currentPage && currentPage.type === "Statement" && (
                    <>
                        {currentPageName === 'Statement230kPlaces' && (
                            <Statement230k
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'TagsStatement' && (
                            <TagsStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection} />
                        )}
                        {currentPageName === 'SafetyRankingStatement' && (
                            <SafetyStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'CommunityValidationStatement' && (
                            <CommunityStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                                previousAnswer={answerHistory[currentPageIndex - 1]}
                            />
                        )}
                        {currentPageName === 'GlobalCoverageStatement' && (
                            <CoverageStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'AggregatedInfoStatement' && (
                            <AggregatedStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'DietitianStatement' && (
                            <DietitianStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'RealtimeUpdatesStatement' && (
                            <RealtimeStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'CrossContaminationStatement' && (
                            <CrossContaminationStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'TravelFeaturesStatement' && (
                            <TravelStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'AtlyIntro1' && (
                            <AtlyIntro
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'SafetyRankingExplanation' && (
                            <SafetyRankingExplanation
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'ReviewAggregationStatement' && (
                            <ReviewAggregationStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'InformationUpdateStatement' && (
                            <InformationUpdateStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                        {currentPageName === 'SafetyCommitmentStatement' && (
                            <SafetyCommitmentStatement
                                data={currentPage}
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                            />
                        )}
                    </>
                )}
                {currentPage && currentPage.type === "PreCheckout" && (
                    <PreCheckout
                        data={currentPage}
                        onAnswer={handleAnswer}
                        formSubmit={formSubmit}
                        trialVariation={trialVariation}
                        isTransitioning={isTransitioning}
                        direction={transitionDirection}
                        userEmail={userEmail}
                        onEmailChange={setUserEmail}
                    />
                )}
                {currentPage && currentPage.type === 'Email' && (
                    <EmailScreen
                        data={{
                            ...currentPage,
                            savedEmail: answerHistory[currentPageIndex]?.text
                        }}
                        onAnswer={handleAnswer}
                        isTransitioning={isTransitioning}
                        direction={transitionDirection}
                    />
                )}
            </div>
        </>
    );
};
